import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormDropDownList, FormNumericTextBox, FormInput, FormSwitch, FormDateTimePicker } from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { GetPermissions } from '../components/Options';

const EditVisitors = () => {
    const { id } = useParams(); // vehicles/id url is used to edit existing vehicle, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [permissions, setPermissions] = React.useState(null);
    const visitorTypes = ["Visitor", "Service Vehicle"];
    const [sites, setSites] = React.useState();
    const navigate = useNavigate();
    if (localStorage.getItem("userid") === null) navigate("/logout");

    // default layout data for page
    const [layout, setLayout] = React.useState([
        { "field": "name", "label": "Name", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "registration", "label": "Registration", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "isActive", "label": "Active", "defaultValue": "true", "isVisible": true, "type": "boolean" },
        { "field": "organisation", "label": "Organization", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "reason", "label": "Reason For Visit", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "visitorType", "label": "Visitor Type", "defaultValue": "", "isVisible": true, "type": "list" },
        { "field": "dateTimeIn", "label": "Time In", "defaultValue": new Date(), "isVisible": true, "type": "datetime" },
        { "field": "dateTimeOut", "label": "Time Out", "defaultValue": new Date(), "isVisible": true, "type": "datetime" },
        { "field": "weighIn", "label": "Weight in", "defaultValue": 0, "isVisible": true, "type": "numeric" },
        { "field": "weighOut", "label": "Weight Out", "defaultValue": 0, "isVisible": true, "type": "numeric" },
        { "field": "phone", "label": "Phone", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "siteId", "label": "Site", "defaultValue": 0, "isVisible": true, "type": "numeric" },
    ]);

    // Gets field from layout based on field name
    const GetField = (f) => {
        return layout.find(obj => { return obj.field === f });
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        name: GetField("name")["defaultValue"],
        registration: GetField("registration")["defaultValue"],
        isActive: GetField("isActive")["defaultValue"] === "true",
        organisation: GetField("organisation")["defaultValue"],
        reason: GetField("reason")["defaultValue"],
        visitorType: GetField("visitorType")["defaultValue"],
        dateTimeIn: GetField("dateTimeIn")["defaultValue"],
        dateTimeOut: GetField("dateTimeOut")["defaultValue"],
        weighIn: GetField("weighIn")["defaultValue"],
        weighOut: GetField("weighOut")["defaultValue"],
        phone: GetField("phone")["defaultValue"],
        siteId: GetField("siteId")["defaultValue"]
    });

    // When page first loads:
    // - populate drop downs from apiv7/options
    // - if in edit mode, set initial values for all fields for apiv7/vehicles
    React.useEffect(() => {
        GetPermissions(["ViewTransaction", "NewTransaction", "EditTransaction"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'editVisitors'";
        const init = {
            method: 'GET',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        fetch(url, init).then(response => response.json()).then(d => {
            if (GetSession().IsMobile) {
                let data = [];
                for (let i = 0; i < d.length; i++) {
                    data.push({ field: d[i].field, label: d[i].label, type: d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile });
                }
                setLayout(data);
            } else {
                setLayout(d);
            }
        });
        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {
            setSites(opt.sites);
            if (id !== undefined) {
                url = configData.SERVER_URL + "apiv7/visitors/" + id;
                fetch(url, init).then(response => response.json()).then(d => {
                    var st = opt.sites.find(obj => { return obj.id === String(d.siteId) });
                    setValues({
                        name: d.name === null ? "" : d.name,
                        registration: d.registration === null ? "" : d.registration,
                        isActive: d.isActive === null ? true : d.isActive,
                        organisation: d.organisation === null ? "" : d.organisation,
                        reason: d.reason === null ? "" : d.reason,
                        visitorType: d.visitorType === null ? "" : d.visitorType,
                        dateTimeIn: d.dateTimeIn === null ? new Date() : new Date(d.dateTimeIn + "Z"),
                        dateTimeOut: d.dateTimeOut === null ? new Date() : new Date(d.dateTimeOut + "Z"),
                        weighIn: d.weighIn === null ? 0 : d.weighIn,
                        weighOut: d.weighOut === null ? 0 : d.weighOut,
                        phone: d.phone === null ? "" : d.phone,
                        siteId: st
                    });
                    setFormkey(new Date());
                });
            }
        });
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return f;
        if (name === "schedulePriceDate" && f === "") f = null;
        if (fld["isVisible"]) return f;
        let res = "";
        switch (fld["type"]) {
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    const isNullOrEmpty = (x) => { return (x === undefined || x === null || x === ""); }

    const Validate = (dataItem) => {
        let st = "";
        if (isNullOrEmpty(dataItem.registration)) st += "Registration\r\n";
        if (isNullOrEmpty(dataItem.name)) st += "Name\r\n";
        if (isNullOrEmpty(dataItem.visitorType)) st += "Visitor Type\r\n";
        if (st !== "") {
            alert("Please enter the following: \r\n" + st);
            return false;
        }
        return true;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        if (!Validate(dataItem)) return;  
        var pth = id === undefined ? "" : "/" + id;
        var url = `${configData.SERVER_URL}apiv7/visitors${pth}`;
        let st = GetDefaultData(dataItem.siteId, "siteId");
        if (st !== undefined) st = st.id;
        const init = {
            method: id === undefined ? 'POST' : 'PUT',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache",
            body: JSON.stringify({
                "visitorId": id === undefined ? 0 : id,
                "name": GetDefaultData(dataItem.name, "name"),
                "registration": GetDefaultData(dataItem.registration, "registration"),
                "organisation": GetDefaultData(dataItem.organisation, "organisation"),
                "phone": GetDefaultData(dataItem.phone, "phone"),
                "visitorType": GetDefaultData(dataItem.visitorType, "visitorType"),
                "weighIn": GetDefaultData(dataItem.weighIn, "weighIn"),
                "weighOut": GetDefaultData(dataItem.weighOut, "weighOut"),
                "dateTimeIn": GetDefaultData(dataItem.dateTimeIn, "timeIn"),
                "dateTimeOut": GetDefaultData(dataItem.dateTimeOut, "timeOut"),
                "reason": GetDefaultData(dataItem.reason, "reason"),
                "isActive": GetDefaultData(dataItem.isActive, "isActive"),
                "siteId": GetSession().SiteID === 1 ? st : GetSession().SiteID 
            })
        }
        fetch(url, init).then(response => response).then(d => {
            var msg = id === undefined ? "New visitor created" : "Visitor updated";
            Redirect(msg);
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
        navigate("/visitors", { state: { msg: msg } });
    }
    if (permissions && !permissions.CanView) navigate("/error401");
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");

    const RegoChanged = (e) => { let v = { ...values }; v.registration = e.value; setValues(v) };
    const ActiveChanged = (e) => { let v = { ...values }; v.isActive = e.value; setValues(v) };
    const NameChanged = (e) => { let v = { ...values }; v.name = e.value; setValues(v) };
    const OrganisationChanged = (e) => { let v = { ...values }; v.organisation = e.value; setValues(v) };
    const PhoneChanged = (e) => { let v = { ...values }; v.phone = e.value; setValues(v) };
    const TypeChanged = (e) => { let v = { ...values }; v.visitorType = e.value; setValues(v) };
    const WeighInChanged = (e) => { let v = { ...values }; v.weighIn = e.value; setValues(v) };
    const WeighOutChanged = (e) => { let v = { ...values }; v.weighOut = e.value; setValues(v) };
    const TimeInChanged = (e) => { let v = { ...values }; v.dateTimeIn = e.value; setValues(v) };
    const TimeOutChanged = (e) => { let v = { ...values }; v.dateTimeOut = e.value; setValues(v) };
    const ReasonChanged = (e) => { let v = { ...values }; v.reason = e.value; setValues(v) };
    const SiteChanged = (e) => { let v = { ...values }; v.siteId = e.value; setValues(v) };

    // form validators
    const textValidator = (value) => !value ? "Please enter a value" : "";

    return (
        <div style={{ padding: 20 }}>
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement style={{ width: "98%" }}>
                    <fieldset className={"k-form-fieldset"}>
                        <div className="row">
                            {GetField("registration")["isVisible"] && <div className="col-md-3">
                                <Field id={"registration"} name={"registration"} label={GetField("registration")["label"]} component={FormInput} validator={textValidator} onChange={RegoChanged} />
                            </div>}
                            {GetSession().SiteID === 1 && GetField("siteId")["isVisible"] && <div className="col-md-3">
                                <Field id={"siteId"} name={"siteId"} label={GetField("siteId")["label"]} component={FormDropDownList} data={sites} textField="name" dataItemKey="id" onChange={SiteChanged} />
                            </div>}
                            {GetField("isActive")["isVisible"] && <div className='col-md-3'>
                                <Field id={"isActive"} name={"isActive"} label={GetField("isActive")["label"]} component={FormSwitch} onChange={ActiveChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("name")["isVisible"] && <div className="col-md-3">
                                <Field id={"name"} name={"name"} label={GetField("name")["label"]} component={FormInput} validator={textValidator} onChange={NameChanged} />
                            </div>}
                            {GetField("organisation")["isVisible"] && <div className="col-md-3">
                                <Field id={"organisation"} name={"organisation"} label={GetField("organisation")["label"]} component={FormInput} onChange={OrganisationChanged} />
                            </div>}
                            {GetField("phone")["isVisible"] && <div className="col-md-3">
                                <Field id={"phone"} name={"phone"} label={GetField("phone")["label"]} component={FormInput} onChange={PhoneChanged} />
                            </div>}
                            {GetField("visitorType")["isVisible"] && <div className='col-md-3'>
                                <Field id={"visitorType"} name={"visitorType"} label={GetField("visitorType")["label"]} data={visitorTypes} component={FormDropDownList} validator={textValidator} onChange={TypeChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("weighIn")["isVisible"] && <div className="col-md-3">
                                <Field id={"weighIn"} name={"weighIn"} label={GetField("weighIn")["label"]} component={FormNumericTextBox} onChange={WeighInChanged} />
                            </div>}
                            {GetField("weighOut")["isVisible"] && <div className="col-md-3">
                                <Field id={"weighOut"} name={"weighOut"} label={GetField("weighOut")["label"]} component={FormNumericTextBox} onChange={WeighOutChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("dateTimeIn")["isVisible"] && <div className="col-md-3">
                                <Field id={"dateTimeIn"} name={"dateTimeIn"} label={GetField("dateTimeIn")["label"]} component={FormDateTimePicker} onChange={TimeInChanged} />
                            </div>}
                            {GetField("dateTimeOut")["isVisible"] && <div className="col-md-3">
                                <Field id={"dateTimeOut"} name={"dateTimeOut"} label={GetField("dateTimeOut")["label"]} component={FormDateTimePicker} onChange={TimeOutChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("reason")["isVisible"] && <div className="col-md-9">
                                <Field id={"reason"} name={"reason"} label={GetField("reason")["label"]} component={FormInput} onChange={ReasonChanged} />
                            </div>}
                        </div>
                    </fieldset>
                    <div className="k-form-buttons" style={{ paddingTop: 10 }}>
                        <div className='col-md-1'>
                            <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit}>{saveName}</Button>
                        </div>
                        <div className='col-md-1'>
                            <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Back to List</Button>
                        </div>
                    </div>
                </FormElement>
            )} />
        </div>
    );
}

export default EditVisitors